import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { SlotPicker } from "@/components/booking/SlotPicker";
import { TYPE_META } from "@/components/booking/TypePicker";

const fmt = (s) => new Date(s).toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

const Status = ({ icon: I, color, title, text, testid }) => (
  <div data-testid={testid} className="text-center space-y-3 py-6">
    <I className={`h-12 w-12 mx-auto ${color}`} />
    <h2 className="text-2xl font-display font-bold">{title}</h2>
    <p className="text-slate-600">{text}</p>
  </div>
);

export default function ManageAppointment() {
  const { token } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  const [moving, setMoving] = useState(false);
  const [slot, setSlot] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(null);

  useEffect(() => { api.get(`/public/appointment/${token}`).then((r) => setA(r.data)).catch(() => setErr(true)); }, [token]);

  const cancel = async () => {
    setBusy(true);
    try { await api.post(`/public/appointment/${token}/cancel`); setState({ kind: "cancelled" }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reschedule = async () => {
    if (!slot) return toast.error("Scegli un nuovo orario");
    setBusy(true);
    try { const r = await api.post(`/public/appointment/${token}/reschedule`, { start: slot }); setState({ kind: "moved", start: r.data.start }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (err) return <div data-testid="appt-not-found" className="min-h-screen grid place-items-center text-slate-500">Appuntamento non trovato.</div>;
  if (!a) return <div className="min-h-screen grid place-items-center text-slate-400">Caricamento…</div>;

  let body;
  if (state?.kind === "cancelled" || a.cancelled) body = <Status testid="appt-cancelled" icon={XCircle} color="text-rose-500" title="Appuntamento annullato" text={`${a.agent_name} è stato avvisato. Puoi prenotarne uno nuovo quando vuoi.`} />;
  else if (state?.kind === "moved") body = <Status testid="appt-moved" icon={CheckCircle2} color="text-emerald-600" title="Appuntamento spostato" text={`Nuovo orario: ${fmt(state.start)}. ${a.agent_name} è stato avvisato.`} />;
  else body = (
    <div className="space-y-6">
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Il tuo appuntamento</p>
        <p className="text-xl font-display font-bold mt-1 capitalize" data-testid="appt-when">{fmt(a.start)}</p>
        <p className="text-sm text-slate-600 mt-1">{TYPE_META[a.type]?.label} · {a.duration} minuti</p>
      </div>
      {!a.can_change ? (
        <p data-testid="appt-locked" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4">
          Mancano meno di 24 ore all'appuntamento: per spostarlo o annullarlo contatta direttamente {a.agent_name}.
        </p>
      ) : moving ? (
        <div className="space-y-5">
          <h3 className="font-display font-semibold">Scegli il nuovo orario</h3>
          <SlotPicker url={`/public/appointment/${token}/slots`} info={a} value={slot} onChange={setSlot} />
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setMoving(false)}>Indietro</Button>
            <Button data-testid="appt-reschedule-confirm" disabled={busy || !slot} onClick={reschedule} className="flex-1 rounded-full bg-[#0F172A] hover:bg-sky-600">Conferma nuovo orario</Button>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          <Button data-testid="appt-move-btn" onClick={() => setMoving(true)} className="h-12 rounded-full bg-[#0F172A] hover:bg-sky-600"><CalendarClock className="h-4 w-4 mr-2" />Sposta appuntamento</Button>
          <Button data-testid="appt-cancel-btn" variant="outline" onClick={() => setConfirmCancel(true)} className="h-12 rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"><XCircle className="h-4 w-4 mr-2" />Annulla appuntamento</Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="manage-appointment-page">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-2xl mx-auto px-6 pt-10 pb-16 flex items-center gap-4">
          {a.picture ? <img src={a.picture} alt="" className="h-14 w-14 rounded-full" /> : <div className="h-14 w-14 rounded-full bg-sky-500 grid place-items-center text-xl font-bold">{a.agent_name[0]}</div>}
          <div>
            <p className="text-sky-300 text-xs uppercase tracking-[0.2em]">Gestisci appuntamento</p>
            <h1 className="text-3xl font-extrabold tracking-tight">{a.agent_name}</h1>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 -mt-8 pb-16">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 fade-up">{body}</div>
      </main>
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare l'appuntamento?</AlertDialogTitle>
            <AlertDialogDescription>{a.agent_name} riceverà un avviso.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, mantieni</AlertDialogCancel>
            <AlertDialogAction data-testid="appt-cancel-confirm" onClick={cancel} className="bg-rose-600 hover:bg-rose-700">Sì, annulla</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
